/**
 * Profile światła narzędzia „Sprzątanie i retusz" — jedyny wybór, jaki użytkownik
 * podejmuje przed generacją.
 *
 * Mieszkają w pakiecie współdzielonym, bo identyfikator (`warm`, `editorial`, …)
 * przechodzi przez granicę HTTP: front wysyła go w `lightingProfile`, API dobiera
 * po nim treść promptu. Dwie osobne listy literałów rozjechałyby się przy pierwszym
 * dołożonym profilu i nie zapaliłoby się to ani w typechecku, ani w teście
 * jednostkowym — front dostałby po cichu profil domyślny zamiast wybranego.
 *
 * Tu są WYŁĄCZNIE identyfikatory i teksty PL dla użytkownika. Angielskie dyrektywy
 * dla modelu zostają po stronie API (prompt/room-cleanup.prompts.ts) — to prompt,
 * nie kontrakt.
 */

export type LightingProfile = "balanced" | "editorial" | "luminous" | "warm";

/**
 * Kolejność = kolejność w interfejsie. Pierwszy jest domyślny.
 *
 * Typ krotki niepustej, a nie zwykła tablica: `z.enum()` po stronie API przyjmuje
 * wyłącznie taki kształt, a bez niego trasa musiałaby rzutować listę w locie —
 * czyli obchodzić kontrolę dokładnie tam, gdzie ona ma działać.
 */
export const LIGHTING_PROFILES: readonly [LightingProfile, ...LightingProfile[]] = [
  "balanced",
  "editorial",
  "luminous",
  "warm",
];

export const DEFAULT_LIGHTING_PROFILE: LightingProfile = "balanced";

export function isLightingProfile(value: string): value is LightingProfile {
  return (LIGHTING_PROFILES as readonly string[]).includes(value);
}

export interface LightingProfileDefinition {
  id: LightingProfile;
  /** Nazwa widziana przez użytkownika i wpisywana w opis transakcji kredytowej. */
  name: string;
  /** Jedno zdanie „po czym poznać" — chip w interfejsie. */
  desc: string;
}

export const LIGHTING_PROFILE_DEFINITIONS: Readonly<Record<LightingProfile, LightingProfileDefinition>> = {
  balanced: { id: "balanced", name: "Zrównoważony HDR", desc: "neutralna biel, równa ekspozycja" },
  editorial: { id: "editorial", name: "Klimat editorial", desc: "jak w magazynie wnętrzarskim" },
  luminous: { id: "luminous", name: "Świetlisty dzienny", desc: "jasno, przestronnie, bez cieni" },
  warm: { id: "warm", name: "Złoty / przytulny", desc: "ciepłe światło, domowy nastrój" },
};

export const LIGHTING_PROFILE_LIST: readonly LightingProfileDefinition[] = LIGHTING_PROFILES.map(
  (id) => LIGHTING_PROFILE_DEFINITIONS[id],
);
